<script lang="ts" setup>
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { i18n } from '@/i18n';
import { computed, ref } from 'vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import MultiTagInput from '@/corePagesModule/components/MultiTagInput.vue';
import { watch } from 'vue';
import AddOptionSection from './AddOptionSection.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { storeToRefs } from 'pinia';
import {
  formOptionsAggregateBusiness,
  inputTypeKeyMap,
  keyMappingForSelectInput,
  unitOptions,
  formOptionsAggregateBusinessUniqueKeys,
} from '../constants';
import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { objectDiff } from '@/utils/objectDiff';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';
import { useUpdateAggregateBusinessForm } from '../composables/useUpdateAggregateBusinessForm';
import { SettingRepository } from '@/coreInfrastructureModule/Repositories/SettingRepository';

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();
const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);

const { getAddableOptions } = store;
const props = withDefaults(
  defineProps<{
    item?: IBusiness;
  }>(),
  { item: undefined }
);

const emits = defineEmits<{
  (e: 'close', modal: string, isNext?: boolean): void;
}>();

const {
  isEdited,
  currentOpenSelectInputName,
  fieldState,
  allValidate,
  createUpdatedAggregateBusiness,
  updateValue,
  updateUserValue,
  addNewOption,
  initializeForm,
} = useUpdateAggregateBusinessForm();

const settingRepository = new SettingRepository();
const aggregateFreeColumnSettings = ref<Omit<BusinessFreeColumnValue, 'value'>[]>([]);
const businessFreeColumnValues = ref<BusinessFreeColumnValue[]>([]);

/**
 * 自由欄設定（集計: columnNo 1-3）を取得し、設定済みの項目だけを保持する
 */
const fetchAggregateFreeColumnSettings = async () => {
  const result = await settingRepository.getBusinessFreeColumnSetting();
  if (result.status.type !== 'success') {
    aggregateFreeColumnSettings.value = [];
    return;
  }
  aggregateFreeColumnSettings.value = result.status.data.aggregate
    .map((item, index) => ({
      columnNo: index + 1,
      headerName: item.headerName,
      isMulti: item.isMulti,
      isConfigured: item.isConfigured ?? false,
      target: 'aggregate' as const,
    }))
    .filter((item) => item.isConfigured);
};

/**
 * 自由欄の入力欄を編集対象の現在値でプリフィルする
 */
const initializeBusinessFreeColumns = () => {
  const sourceValues = state.value.isCreatingBoth
    ? state.value.item?.businessFreeColumnValues
    : state.value.item?.businessFreeColumnValues ?? props.item?.businessFreeColumnValues;
  businessFreeColumnValues.value = aggregateFreeColumnSettings.value.map((setting) => ({
    ...setting,
    value:
      sourceValues?.find(
        (column) =>
          (column.target === 'aggregate' || (!state.value.isCreatingBoth && column.target === undefined)) &&
          column.columnNo === setting.columnNo
      )?.value ?? '',
  }));
};

/**
 * 自由欄の入力値を更新する
 * @param index - 自由欄配列のindex
 * @param value - 入力値
 */
const updateBusinessFreeColumnValue = (index: number, value: string) => {
  const target = businessFreeColumnValues.value[index];
  if (!target) return;
  target.value = value;
  isEdited.value = true;
};

const aggregateFreeColumnSettingsReady = fetchAggregateFreeColumnSettings();

const modalText = computed<{ title: string; desc: string }>(() => {
  if (state.value.isCreatingBoth) {
    return {
      title: t('追加する集計取引情報'),
      desc: t('関連取引と同時に追加する集計取引情報を入力してください。'),
    };
  } else if (state.value.isCreateMode) {
    return {
      title: t('取引追加'),
      desc: t('新規に取引を追加できます。'),
    };
  } else {
    return {
      title: t('取引編集'),
      desc: t('選択済の取引ファイルに対して、項目の内容を修正できます。'),
    };
  }
});

/**
 * 編集モーダルの保存ボタン押下時の処理
 */
const editingModalOnNext = async () => {
  if (!allValidate()) return;
  const updatedItem = createUpdatedAggregateBusiness(props.item);
  const baseFreeColumnValues = state.value.isCreatingBoth
    ? state.value.item?.businessFreeColumnValues?.filter((column) => column.target === 'base') ?? []
    : [];
  updatedItem.businessFreeColumnValues = [
    ...baseFreeColumnValues.map((column) => ({ ...column })),
    ...businessFreeColumnValues.value.map((column) => ({ ...column })),
  ];
  const updatedDiff = objectDiff(props.item ?? {}, updatedItem);
  state.value.updatedItemDiff = updatedDiff.first;
  state.value.item = updatedItem;
  emits('close', 'aggregateBusinessEdit', true);
};

/**
 * フォームのリストを見た目上で分かれている部分で分割して取得する
 */
const getDividedFormOptions = computed(() => {
  return [formOptionsAggregateBusiness.slice(0, 11), formOptionsAggregateBusiness.slice(11)];
});

/**
 * モーダルを開いた際の処理
 */
watch(
  () => state.value.modalState.aggregateBusinessEdit,
  async (newVal) => {
    if (newVal && props.item) {
      await aggregateFreeColumnSettingsReady;
      if (!state.value.modalState.aggregateBusinessEdit) return;
      initializeForm(props.item, state.value.item);
      initializeBusinessFreeColumns();
    }
  }
);

/**
 * inputのgrid幅指定クラス取得
 * @param option - inputの対応名
 * @param index - インデックス
 */
const getBaseDataItemClass = (option: string, index: number) => {
  if (index < 5) {
    return 'modal__item--base-data--upper';
  } else if (option === 'package' || option === 'unit') {
    return 'modal__item--base-data--number';
  } else {
    return 'modal__item--base-data--lower';
  }
};

/**
 * 自由入力カラムの編集状態を変更する
 */
const markBusinessFreeColumnEdited = () => {
  isEdited.value = true;
};
</script>
<template>
  <InfoModal
    :modal-state="state.modalState.aggregateBusinessEdit"
    :modal-width="1300"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('保存')"
    :is-disabled-next-button="!isEdited"
    @on-back="
      () => {
        emits('close', 'aggregateBusinessEdit', false);
      }
    "
    @on-next="editingModalOnNext"
  >
    <template #title>{{ modalText.title }}</template>
    <template #description>{{ modalText.desc }}</template>
    <template #default>
      <div class="modal__container modal__container--base-data">
        <template v-for="(option, index) in getDividedFormOptions[0]" :key="option">
          <div class="modal__item" :class="getBaseDataItemClass(option, index)">
            <template v-if="inputTypeKeyMap[option] === 'select'">
              <SingleSelectInput
                :index="index"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                :disabled="state.isCreatingBoth && formOptionsAggregateBusinessUniqueKeys.includes(option)"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
            <template v-else-if="inputTypeKeyMap[option] === 'selectUser'">
              <MultipleUserSelectInput
                :select-input-index="index"
                :label="t(option)"
                :placeholder="t(option)"
                :original-selected-user-id-list="fieldState[option].value.value ? fieldState[option].value.value.split(',') : []"
                @update:model-value="(e) => updateUserValue(option, e)"
              />
            </template>
            <template v-else>
              <TextInput
                :model-value="fieldState[option].value.value"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :placeholder="t(option)"
                :is-error="fieldState[option].isError.value"
                :error-message="t(fieldState[option].errorMessage.value)"
                ensure-error-msg-height
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
          </div>
        </template>
      </div>
      <div class="modal__container modal__container--amount">
        <template v-for="(option, index) in getDividedFormOptions[1]" :key="option">
          <div class="modal__item">
            <template v-if="inputTypeKeyMap[option] === 'select'">
              <SingleSelectInput
                :index="index + getDividedFormOptions[0].length"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
            <template v-if="inputTypeKeyMap[option] === 'selectUnit'">
              <SingleSelectInput
                :index="index + getDividedFormOptions[0].length"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                :prop-options="unitOptions.map((o) => t(o))"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
            <template v-else>
              <TextInput
                :model-value="fieldState[option].value.value"
                :label="
                  option === 'MOQ'
                    ? t('取引編集MOQ')
                    : option === 'inTransitDays'
                    ? `${t(option)}（${t('日')}）`
                    : t(option) + updateBusinessVM.getRequiredMark(option)
                "
                :placeholder="t(option)"
                :is-error="fieldState[option].isError.value"
                :error-message="t(fieldState[option].errorMessage.value)"
                ensure-error-msg-height
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
          </div>
        </template>
      </div>
      <div v-if="businessFreeColumnValues.length > 0" class="modal__container modal__container--free-column">
        <template v-for="(freeColumn, index) in businessFreeColumnValues" :key="freeColumn.columnNo">
          <div class="modal__item">
            <MultiTagInput
              v-if="freeColumn.isMulti"
              :model-value="freeColumn.value"
              :label="freeColumn.headerName"
              :placeholder="freeColumn.headerName"
              @editing="markBusinessFreeColumnEdited"
              @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
            />
            <TextInput
              v-else
              :model-value="freeColumn.value"
              :label="freeColumn.headerName"
              :placeholder="freeColumn.headerName"
              ensure-error-msg-height
              @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
            />
          </div>
        </template>
      </div>
      <!-- 新規選択肢追加 -->
      <AddOptionSection is-aggregate-business :options="getAddableOptions(true)" @set-option="addNewOption" />
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;

    &--base-data {
      grid-template-columns: repeat(20, 1fr);
    }

    &--amount {
      grid-template-columns: repeat(7, 1fr);
    }

    &--free-column {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  &__item {
    display: grid;
    grid-template-columns: 100%;
    height: fit-content;

    box-sizing: border-box;
    border-bottom: solid 1px $borderDefault;

    &--base-data {
      &--upper {
        grid-column: span 5;
      }

      &--lower {
        grid-column: span 4;
      }

      &--number {
        grid-column: span 2;
      }
    }
  }
}
</style>
